import React from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiHealth, EuiHideFor, EuiImage, EuiLink, EuiText, EuiToolTip } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { Creative, CreativeStatus } from 'api/interfaces'
import { CreativeStatusToElasticColor, CreativeStatusToString } from 'utils/EnumToFriendly'

import { AdHoverPreview } from '../AdHoverPreview'
import { AdState } from '../campaignSlice'

export const toAdState = (creative: Creative): AdState => ({
  id: creative.id,
  type: creative.type,
  checked: creative.status !== CreativeStatus.Inactive && creative.status !== CreativeStatus.Deleted,
  status: creative.status,
  disabled: !creative.canEditStatus,
  canRemove: creative.canRemove,
  rejectionReasons: creative.auditReasons,
  name: creative.name ?? '',
  headline: creative.nativeDetails?.headline,
  body: creative.nativeDetails?.body,
  thumbnailUrl: creative.thumbnailUrl ? creative.thumbnailUrl : creative.displayAdDetails?.uploadedUrl,
  sections: creative.tvCommercialDetails?.sections,
  loading: false,
  mediaType: creative.mediaType,
  uploadedUrl: creative.tvCommercialUploadedDetails?.webOptimizedUrl ?? creative.tvCommercialUploadedDetails?.uploadedUrl!,
  destinationUrl: creative.destinationUrl
})

interface AdPreviewCellProps {
  ad: AdState
  showHoverPreview: boolean
}

export const AdPreviewCell: React.FC<AdPreviewCellProps> = ({ ad, showHoverPreview }) => (
  <EuiFlexGroup responsive={false} style={{ minWidth: 270 }} gutterSize='none'>
    <EuiHideFor sizes={['xs']}>
      <EuiFlexItem grow={false} style={{ width: 50, minWidth: 50 }} className='eui-alignMiddle'>
        <AdHoverPreview enabled={showHoverPreview} imageUrl={ad.thumbnailUrl}>
          <EuiImage alt='ad' url={ad.thumbnailUrl || '/82x82.png'} size='fullWidth' style={{ borderRadius: 12 }} />
        </AdHoverPreview>
      </EuiFlexItem>
    </EuiHideFor>
    <EuiFlexItem grow={false} style={{ marginLeft: 10, width: 200 }}>
      <EuiText size='xs' style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
        <strong>{ad.headline}</strong>
      </EuiText>
      <EuiText size='xs' color='subdued' style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
        {ad.body}
      </EuiText>
    </EuiFlexItem>
  </EuiFlexGroup>
)

const adPreviewColumn = (showHoverPreview: boolean): EuiBasicTableColumn<AdState> => ({
  width: '240px',
  name: 'Ad',
  render: (ad: AdState) => <AdPreviewCell ad={ad} showHoverPreview={showHoverPreview} />
})

const adNameColumn: EuiBasicTableColumn<AdState> = {
  width: '240px',
  name: 'Name',
  render: (ad: AdState) => ad.name
}

const adUrlColumn: EuiBasicTableColumn<AdState> = {
  name: 'URL',
  render: (ad: AdState) =>
    ad.destinationUrl ? (
      <EuiLink href={ad.destinationUrl} target='_blank' style={{ wordBreak: 'break-all' }}>
        {ad.destinationUrl}
      </EuiLink>
    ) : (
      <EuiText size='xs' color='subdued'>
        None
      </EuiText>
    )
}

const adStatusColumn: EuiBasicTableColumn<AdState> = {
  width: '110px',
  name: 'Status',
  render: (creative: AdState) => {
    const color = CreativeStatusToElasticColor(creative.status)
    const label = CreativeStatusToString(creative.status)

    return creative.status === CreativeStatus.Disapproved ? (
      <EuiToolTip
        position='right'
        content={
          <EuiText>
            <ul>
              {creative.rejectionReasons.map(reason => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </EuiText>
        }>
        <EuiHealth color={color}>{label}</EuiHealth>
      </EuiToolTip>
    ) : (
      <EuiHealth color={color}>{label}</EuiHealth>
    )
  }
}

export const buildAdColumns = (adStates: AdState[], showDestinationUrl: boolean, showHoverPreview: boolean): Array<EuiBasicTableColumn<AdState>> => [adPreviewColumn(showHoverPreview), ...(adStates.some(ad => ad.mediaType === 'Display') ? [adNameColumn] : []), ...(showDestinationUrl ? [adUrlColumn] : []), adStatusColumn]
